import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { CheckCircle, XCircle, MinusCircle, Trophy, ArrowRight } from "@phosphor-icons/react";
import { RadialBarChart, RadialBar, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function MCQResults() {
    const { sessionId } = useParams();
    const [r, setR] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/mcq/session/${sessionId}/result`);
                setR(data);
            } catch {
                navigate("/mcq");
            }
        })();
    }, [sessionId, navigate]);

    if (!r) return <Layout><div className="text-slate-500">Loading…</div></Layout>;

    const chartData = [{ name: "acc", value: r.accuracy, fill: "#1E3A8A" }];
    const barData = Object.entries(r.subject_stats || {}).map(([k, v]) => ({ subject: k, correct: v.correct, wrong: v.wrong, unattempted: v.unattempted }));

    return (
        <Layout>
            <div className="mb-8">
                <div className="text-xs uppercase tracking-widest text-slate-500">Test Result</div>
                <h1 className="font-heading font-bold text-3xl sm:text-4xl mt-1 text-slate-900">
                    <span className="text-gradient-navy">{r.accuracy}%</span> · {r.correct} correct
                </h1>
                <p className="text-slate-500 mt-2 text-sm">{r.exam_id.replace("_", " ").toUpperCase()} · {r.subject} · submitted {new Date(r.submitted_at).toLocaleString()}</p>
            </div>

            <div className="grid md:grid-cols-4 gap-5">
                <div className="bento-card md:col-span-2 flex items-center gap-6">
                    <div className="w-40 h-40 shrink-0">
                        <ResponsiveContainer>
                            <RadialBarChart cx="50%" cy="50%" innerRadius="70%" outerRadius="100%" barSize={12} data={chartData} startAngle={90} endAngle={-270}>
                                <RadialBar background={{ fill: "#E2E8F0" }} dataKey="value" cornerRadius={10} />
                            </RadialBarChart>
                        </ResponsiveContainer>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-widest text-slate-500">Score</div>
                        <div className="font-mono text-5xl font-bold text-[#1E3A8A]" data-testid="result-score">{r.score}<span className="text-2xl text-slate-400">/{r.total}</span></div>
                        <div className="mt-3 text-sm text-slate-500">Time taken: {Math.floor(r.time_taken_seconds / 60)}m {r.time_taken_seconds % 60}s</div>
                    </div>
                </div>
                <Stat icon={CheckCircle} label="Correct" val={r.correct} color="text-emerald-500" />
                <Stat icon={XCircle} label="Wrong" val={r.wrong} color="text-red-500" />
                <Stat icon={MinusCircle} label="Skipped" val={r.unattempted} color="text-slate-500" />

                {barData.length > 0 && (
                    <div className="md:col-span-4 bento-card">
                        <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Subject Breakdown</div>
                        <div className="h-64">
                            <ResponsiveContainer>
                                <BarChart data={barData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                    <XAxis dataKey="subject" stroke="#64748B" fontSize={11} />
                                    <YAxis stroke="#64748B" fontSize={11} allowDecimals={false} />
                                    <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 8, color: "#0F172A" }} />
                                    <Bar dataKey="correct" stackId="a" fill="#10B981" />
                                    <Bar dataKey="wrong" stackId="a" fill="#EF4444" />
                                    <Bar dataKey="unattempted" stackId="a" fill="#CBD5E1" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}
            </div>

            {/* Review */}
            <div className="mt-8">
                <h2 className="font-heading text-2xl font-bold mb-4 text-slate-900">Answer Review</h2>
                <div className="space-y-4">
                    {r.review.map((rev, i) => (
                        <div key={rev.question_id} className={`bento-card border-l-4 ${rev.is_correct ? "border-l-emerald-500" : rev.your_answer === null || rev.your_answer === undefined ? "border-l-slate-400" : "border-l-red-500"}`}>
                            <div className="flex justify-between items-start gap-4">
                                <div className="text-[11px] uppercase tracking-widest text-slate-500">{rev.subject} · Q{i + 1}</div>
                                <div className={`text-xs px-2 py-0.5 rounded-full ${rev.is_correct ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
                                    {rev.is_correct ? "Correct" : rev.your_answer === null || rev.your_answer === undefined ? "Skipped" : "Wrong"}
                                </div>
                            </div>
                            <div className="font-heading text-lg mt-2 text-slate-900">{rev.question}</div>
                            <div className="mt-3 grid sm:grid-cols-2 gap-2">
                                {rev.options.map((opt, j) => {
                                    const isCorrect = j === rev.correct_answer;
                                    const isYour = j === rev.your_answer;
                                    return (
                                        <div key={j} className={`px-3 py-2 rounded-lg text-sm border flex items-center gap-2 ${
                                            isCorrect ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                                            : isYour ? "bg-red-50 border-red-300 text-red-800"
                                            : "bg-slate-50 border-[#BFD3FF] text-slate-600"
                                        }`}>
                                            <span className="font-mono text-xs">{String.fromCharCode(65 + j)}.</span>
                                            <span>{opt}</span>
                                            {isCorrect && <CheckCircle size={14} className="ml-auto text-emerald-600" weight="fill" />}
                                        </div>
                                    );
                                })}
                            </div>
                            {rev.explanation && (
                                <div className="mt-3 p-3 rounded-lg bg-blue-50/60 border border-blue-100 text-sm text-slate-700">
                                    <span className="text-[#1E3A8A] font-semibold">Explanation: </span>{rev.explanation}
                                </div>
                            )}
                            {rev.source_url && (
                                <a href={rev.source_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#1E3A8A] hover:underline">
                                    Read source article ({rev.source_paper}) →
                                </a>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="mt-10 flex gap-3">
                <button data-testid="new-test-btn" onClick={() => navigate("/mcq")} className="btn-primary">Take another test <ArrowRight size={18} /></button>
                <button onClick={() => navigate("/dashboard")} className="btn-ghost">Back to dashboard</button>
            </div>
        </Layout>
    );
}

function Stat({ icon: Icon, label, val, color }) {
    return (
        <div className="bento-card">
            <Icon size={22} weight="duotone" className={color} />
            <div className="text-xs uppercase tracking-widest text-slate-500 mt-3">{label}</div>
            <div className="font-mono text-3xl font-bold mt-1 text-slate-900">{val}</div>
        </div>
    );
}
